import { useState } from 'react';
import {
  Card, CardBody, Badge, Button, Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Row, Col,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useKnowledgeEntries, useKnowledgeCandidates,
  useSaveEntry, useDeleteEntry, usePromoteCandidate, useDismissCandidate,
} from './useKnowledge';

const SEV_COLORS = { info: 'secondary', low: 'secondary', medium: 'info', high: 'warning', critical: 'danger' };
const SEV_OPTIONS = ['info', 'low', 'medium', 'high', 'critical'];

function SevBadge({ severity }) {
  return <Badge color={SEV_COLORS[severity] ?? 'secondary'} pill>{severity}</Badge>;
}

const EMPTY = {
  id: null, kind: 'error', title: '', software: '', app_slug: '',
  match_type: 'substring', pattern: '', severity: 'medium',
  explanation_md: '', solution_md: '', article_url: '', published: true,
};

function EntryEditor({ open, initial, onClose, canWrite }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const save = useSaveEntry();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    const payload = {
      ...(form.id ? { id: form.id } : {}),
      kind: form.kind, title: form.title.trim(), software: form.software.trim() || 'generic',
      app_slug: form.app_slug.trim() || null, match_type: form.match_type,
      pattern: form.pattern, severity: form.severity,
      explanation_md: form.explanation_md, solution_md: form.solution_md || null,
      article_url: form.article_url || null, published: form.published,
    };
    await save.mutateAsync(payload);
    onClose();
  };

  const valid = form.title.trim() && form.pattern.trim() && form.explanation_md.trim();

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>{form.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={8}>
            <FormGroup>
              <Label>Titel *</Label>
              <Input value={form.title} onChange={set('title')} placeholder="z. B. MariaDB: io_uring unter gVisor blockiert" />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Art</Label>
              <Input type="select" value={form.kind} onChange={set('kind')}>
                <option value="error">errorDB (Fehler)</option>
                <option value="log">LogDB (Log-Zeile)</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={5}>
            <FormGroup>
              <Label>Software</Label>
              <Input value={form.software} onChange={set('software')} placeholder="mariadb, apache, wordpress …" />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>App-Slug (optional Scope)</Label>
              <Input value={form.app_slug} onChange={set('app_slug')} placeholder="leer = global" />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label>Schweregrad</Label>
              <Input type="select" value={form.severity} onChange={set('severity')}>
                {SEV_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormGroup>
              <Label>Match-Typ</Label>
              <Input type="select" value={form.match_type} onChange={set('match_type')}>
                <option value="substring">Substring (enthält)</option>
                <option value="regex">Regex</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={8}>
            <FormGroup>
              <Label>Muster / Log-Zeile *</Label>
              <Input value={form.pattern} onChange={set('pattern')} className="font-monospace" placeholder="Text der in der Log-Zeile vorkommt" />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label>Erklärung (Markdown) *</Label>
          <Input type="textarea" rows={5} value={form.explanation_md} onChange={set('explanation_md')} />
        </FormGroup>
        <FormGroup>
          <Label>Lösung (Markdown, optional)</Label>
          <Input type="textarea" rows={4} value={form.solution_md} onChange={set('solution_md')} />
        </FormGroup>
        <Row>
          <Col md={8}>
            <FormGroup>
              <Label>Weiterführender Artikel (URL, optional)</Label>
              <Input value={form.article_url} onChange={set('article_url')} placeholder="https://…" />
            </FormGroup>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <FormGroup switch className="mb-3">
              <Input type="switch" id="kb-pub" checked={form.published} onChange={set('published')} />
              <Label check for="kb-pub">Veröffentlicht</Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={onClose}>Abbrechen</Button>
        <Button color="primary" disabled={!valid || !canWrite || save.isPending} onClick={submit}>
          {save.isPending ? 'Speichere …' : 'Speichern'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function EntriesTab({ kind, canWrite }) {
  const query = useKnowledgeEntries(kind);
  const del = useDeleteEntry();
  const [editor, setEditor] = useState(null); // null | {open, initial}
  const items = query.data?.items ?? [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">{items.length} Einträge</span>
        {canWrite && (
          <Button size="sm" color="primary" onClick={() => setEditor({ initial: { ...EMPTY, kind } })}>
            + Neuer Eintrag
          </Button>
        )}
      </div>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Noch keine Einträge. Über die Kandidaten-Inbox oder „Neuer Eintrag“ befüllen."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 90 }}>Severity</th>
            <th>Titel</th>
            <th style={{ width: 130 }}>Software</th>
            <th style={{ width: 90 }}>Match</th>
            <th style={{ width: 90 }}>Status</th>
            <th style={{ width: 140 }} className="text-end">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id}>
              <td><SevBadge severity={e.severity} /></td>
              <td>
                {e.title}
                <div className="small text-muted font-monospace text-truncate" style={{ maxWidth: 380 }} title={e.pattern}>{e.pattern}</div>
              </td>
              <td className="small">{e.software}{e.app_slug ? <span className="text-muted"> · {e.app_slug}</span> : null}</td>
              <td className="small text-muted">{e.match_type}</td>
              <td>{e.published ? <Badge color="success" pill>live</Badge> : <Badge color="secondary" pill>Entwurf</Badge>}</td>
              <td className="text-end">
                {canWrite && (
                  <>
                    <Button size="sm" color="link" onClick={() => setEditor({ initial: { ...EMPTY, ...e, app_slug: e.app_slug ?? '', solution_md: e.solution_md ?? '', article_url: e.article_url ?? '' } })}>
                      Bearbeiten
                    </Button>
                    <Button size="sm" color="link" className="text-danger" disabled={del.isPending}
                      onClick={() => { if (window.confirm(`„${e.title}“ wirklich löschen?`)) del.mutate({ id: e.id }); }}>
                      Löschen
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {editor && (
        <EntryEditor open initial={editor.initial} canWrite={canWrite} onClose={() => setEditor(null)} />
      )}
    </div>
  );
}

function CandidatesTab({ canWrite }) {
  const query = useKnowledgeCandidates();
  const dismiss = useDismissCandidate();
  const [editor, setEditor] = useState(null);
  const items = query.data?.items ?? [];

  // Kandidat → Editor vorbefüllen (exaktes Sample als Substring-Muster).
  const promote = (c) => setEditor({
    initial: {
      ...EMPTY, kind: 'error', software: c.software ?? '', app_slug: c.app_slug ?? '',
      match_type: 'substring', pattern: c.sample_line ?? '',
      title: (c.sample_line ?? '').slice(0, 80),
    },
  });

  return (
    <div>
      <p className="text-muted small">
        Automatisch gesammelte, noch unbekannte fehlerartige Log-Zeilen (nach Häufigkeit). „Übernehmen“ öffnet den
        Editor mit vorbefülltem Muster; „Verwerfen“ blendet die Zeile dauerhaft aus.
      </p>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine offenen Kandidaten. 🎉"
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }} className="text-end">Anzahl</th>
            <th>Beispiel-Zeile</th>
            <th style={{ width: 120 }}>Software</th>
            <th style={{ width: 120 }}>Zuletzt</th>
            <th style={{ width: 180 }} className="text-end">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td className="text-end"><Badge color="dark" pill>{c.count}</Badge></td>
              <td className="small font-monospace text-truncate" style={{ maxWidth: 420 }} title={c.sample_line}>{c.sample_line}</td>
              <td className="small">{c.software ?? '—'}{c.app_slug ? <span className="text-muted"> · {c.app_slug}</span> : null}</td>
              <td><RelativeTime iso={c.last_seen} /></td>
              <td className="text-end">
                {canWrite && (
                  <>
                    <Button size="sm" color="primary" outline onClick={() => promote(c)}>Übernehmen</Button>{' '}
                    <Button size="sm" color="link" className="text-muted" disabled={dismiss.isPending}
                      onClick={() => dismiss.mutate({ id: c.id })}>Verwerfen</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {editor && (
        <EntryEditor open initial={editor.initial} canWrite={canWrite} onClose={() => setEditor(null)} />
      )}
    </div>
  );
}

export function KnowledgeBasePage() {
  const [tab, setTab] = useState('error');
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  return (
    <div>
      <PageHeader
        title="Wissensdatenbank"
        subtitle="Kuration von errorDB (Fehler) & LogDB (Log-Zeilen) + Kandidaten-Inbox"
      />
      <Card>
        <CardBody>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink active={tab === 'error'} onClick={() => setTab('error')} style={{ cursor: 'pointer' }}>errorDB</NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={tab === 'log'} onClick={() => setTab('log')} style={{ cursor: 'pointer' }}>LogDB</NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={tab === 'candidates'} onClick={() => setTab('candidates')} style={{ cursor: 'pointer' }}>Kandidaten-Inbox</NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={tab}>
            <TabPane tabId="error">{tab === 'error' && <EntriesTab kind="error" canWrite={canWrite} />}</TabPane>
            <TabPane tabId="log">{tab === 'log' && <EntriesTab kind="log" canWrite={canWrite} />}</TabPane>
            <TabPane tabId="candidates">{tab === 'candidates' && <CandidatesTab canWrite={canWrite} />}</TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
}
